import { BackgroundColors, BackgroundColorNameKeys } from '@strength/domain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@mui/material';
import { MdCheck, MdExpandMore, MdExpandLess } from '@teamworksdev/icons';
import { cn } from '../../../../components/utils';

export const SWATCH_COUNT = BackgroundColors.length + 1;

export function nextColorValue(
  current: string | null,
  clicked: string | null,
): string | null {
  if (clicked === null) return null;
  return current === clicked ? null : clicked;
}

type ColorSwatchPickerProps = {
  value: string | null;
  onChange: (color: string | null) => void;
  label?: string;
};

export function ColorSwatchPicker({
  value,
  onChange,
  label,
}: ColorSwatchPickerProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (color: string | null) => {
    onChange(nextColorValue(value, color));
    handleClose();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          value
            ? t('selectColor', {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                name: t((BackgroundColorNameKeys[value] ?? 'color') as any),
              })
            : t('color')
        }
        className={cn(
          'flex items-center gap-2 h-9 px-3 border rounded-[6px] w-[82px] bg-white overflow-hidden cursor-pointer',
          open
            ? 'border-blue-600 shadow-[0_0_0_3px_theme(colors.informational.25)]'
            : 'border-gray-300',
        )}
      >
        <div
          className={cn(
            'w-[22px] h-[22px] rounded-full shrink-0',
            !value && 'border-2 border-dashed border-gray-300',
          )}
          style={value ? { backgroundColor: value } : undefined}
        />
        {open ? (
          <MdExpandLess aria-hidden="true" size={20} />
        ) : (
          <MdExpandMore aria-hidden="true" size={20} />
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { borderRadius: '6px', mt: '4px' },
          },
        }}
      >
        <div
          className="p-3 flex flex-col gap-2"
          role="listbox"
          aria-label={label ?? t('color')}
        >
          <div className="flex flex-wrap gap-2">
            {BackgroundColors.map((color) => {
              const isSelected = value === color;
              return (
                <button
                  key={color}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-label={t('selectColor', {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name: t((BackgroundColorNameKeys[color] ?? 'color') as any),
                  })}
                  className="relative w-[22px] h-[22px] rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500"
                  style={{ backgroundColor: color }}
                  onClick={() => handleSelect(color)}
                >
                  {isSelected && (
                    <span
                      className="absolute inset-0 flex items-center justify-center"
                      aria-hidden="true"
                    >
                      <MdCheck size={14} color="black" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </Popover>
    </>
  );
}
